import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import heic2any from 'heic2any';
import exifr from 'exifr';
import {
  ordersApi,
  sessionsApi,
  photosApi,
  aiApi,
  uploadToSignedUrl,
  type LayoutPlan,
} from '@/lib/api';
import { analyzePhoto, type PhotoAnalysis } from '@/lib/photoAnalyzer';
import { normalizeJpegOrientation } from '@/lib/normalizeOrientation';
import { sha256Hex } from '@/lib/contentHash';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { detectDevice } from '@/lib/detectDevice';
import { useAuthStore } from '@/store/useAuthStore';
import { isIosDevice, galleryAccept } from '@/lib/iosPicker';
import { classifyUploadSelection, uploadNoticeFor } from '@/lib/uploadFileFilter';
import { putOrCleanupOrphan } from '@/lib/uploadOrphanCleanup';
import { uploadViaTus } from '@/lib/tusUpload';
import { IosUploadNote } from '@/components/IosUploadNote';
import { HelpFAB } from '@/components/HelpFAB';
import { UploadedTileGrid } from '@/components/upload/UploadedTileGrid';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_PHOTOS = 300;
// iOS per-pick cap (parity with AlbumFlowPage/MagicUploadPage). >20 fresh HEIC
// originals in a single iPhone pick blow the tab's RAM (Safari silently reloads
// and loses the selection). Cap to 20 on iOS only and ask for the rest in a
// follow-up batch. Non-iOS keeps the full pick.
const IOS_PICK_CAP = 20;
const CONCURRENT_UPLOADS = 10;
const HEIC_CONCURRENT = 2; // Max simultaneous HEIC conversions (WASM single-threaded)
const MAX_PHOTO_DIM = 3600; // Max pixel dimension for uploaded images (3600px = 300 DPI A4)
const JPEG_QUALITY = 0.88; // Quality for resized output (unified across upload paths)
const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UploadingPhoto {
  id: string;
  /** Heavy source bytes. Released (set to null) once the upload + analysis
   *  confirm, so a large batch doesn't pin every original in RAM on iPhone
   *  (parity with AlbumFlowPage's source release). Only ever null AFTER 'done';
   *  no live path re-reads it past that point. */
  file: File | null;
  /** Light dedup key (name+size+lastModified) captured at intake so client-side
   *  dedup survives after `file` is released to null. */
  key: string;
  /** Original filename — kept for error logging after `file` is released. */
  name: string;
  preview: string;
  status: 'pending' | 'converting' | 'uploading' | 'analyzing' | 'done' | 'error';
  progress: number;
  error?: string;
  analysis?: PhotoAnalysis;
}

// ─── Conversion Semaphore ─────────────────────────────────────────────────────
// Limits concurrent HEIC conversions so WASM doesn't get overloaded

class ConversionSemaphore {
  private slots: number;
  private queue: (() => void)[] = [];
  constructor(limit: number) {
    this.slots = limit;
  }
  acquire(): Promise<void> {
    if (this.slots > 0) {
      this.slots--;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => this.queue.push(resolve));
  }
  release() {
    if (this.queue.length > 0) {
      this.queue.shift()!();
    } else {
      this.slots++;
    }
  }
}

const heicSemaphore = new ConversionSemaphore(HEIC_CONCURRENT);
const heicConversionCache = new Map<string, Promise<File>>();

// ─── HEIC Thumbnail Extraction ────────────────────────────────────────────────
// iPhones embed a JPEG thumbnail inside every HEIC file — extract it instantly
// without full HEIC decoding. Average time: < 50ms vs 2-10s for full conversion.

async function extractHeicThumbnail(file: File): Promise<string | null> {
  try {
    const thumb = await exifr.thumbnail(file);
    if (thumb && thumb.byteLength > 100) {
      const blob = new Blob([thumb as BlobPart], { type: 'image/jpeg' });
      return URL.createObjectURL(blob);
    }
  } catch {
    // thumbnail not found or parse error — will use grey placeholder
  }
  return null;
}

// ─── Canvas JPEG Resize ───────────────────────────────────────────────────────
// Resize after heic2any conversion — reduces 12-48MP files to ≤3600px
// This dramatically shrinks upload size and improves editor performance.

async function resizeJpegBlob(blob: Blob, maxDim: number, quality: number): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;
      // Skip resize if already small enough
      if (w <= maxDim && h <= maxDim) {
        resolve(blob);
        return;
      }
      const scale = maxDim / Math.max(w, h);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (result) => resolve(result ?? blob),
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(blob); // fallback: return original
    };
    img.src = url;
  });
}

// ─── HEIC Pre-conversion ──────────────────────────────────────────────────────
// All HEIC files start converting immediately (semaphore-limited to HEIC_CONCURRENT).
// Result is cached so the upload pipeline just awaits the already-running promise.

function startHeicConversion(localId: string, file: File): Promise<File> {
  if (heicConversionCache.has(localId)) return heicConversionCache.get(localId)!;

  const p = (async () => {
    // Wait for a semaphore slot (max HEIC_CONCURRENT at a time)
    await heicSemaphore.acquire();
    try {
      const rawBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      const jpegBlob = Array.isArray(rawBlob) ? rawBlob[0] : rawBlob;
      // Resize to max MAX_PHOTO_DIM to reduce upload size
      const resized = await resizeJpegBlob(jpegBlob, MAX_PHOTO_DIM, JPEG_QUALITY);
      const baseName = file.name.replace(/\.(heic|heif)$/i, '');
      return new File([resized], `${baseName}.jpg`, { type: 'image/jpeg' });
    } finally {
      heicSemaphore.release();
    }
  })();

  heicConversionCache.set(localId, p);
  return p;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const headers: Record<string, string> = {};
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
}

function resolveContentType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'heic': return 'image/heic';
    case 'heif': return 'image/heif';
    case 'jpg': case 'jpeg': return 'image/jpeg';
    case 'png': return 'image/png';
    case 'webp': return 'image/webp';
    default: return 'image/jpeg';
  }
}

function isHeicFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

/** Unique key for dedup — name + size + lastModified (virtually collision-free) */
function fileKey(file: File): string {
  return `${file.name}__${file.size}__${file.lastModified}`;
}

function generateLocalId(): string {
  return crypto.randomUUID();
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PhotoUploadPage() {
  const { shopifyOrderId, albumIndex: albumIndexParam } = useParams<{
    shopifyOrderId: string;
    albumIndex: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Came from the editor's "+ Ajouter" (adding to an EXISTING album) → the CTA
  // is "Ajouter à l'album" (just return; photos are already uploaded to the
  // session), NOT "Générer l'album" (which AI-regenerates + wipes the layout).
  const isAddMode = searchParams.get('from') === 'editor';

  const albumIndex = parseInt(albumIndexParam ?? '1', 10);

  // Capture (once) the customer's device on the upload surface. Worker writes
  // orders.device only while null (first device wins). Admins excluded; they
  // use the editor, not this page. Fire-and-forget — errors swallowed.
  const isAdmin = useAuthStore((s) => s.profile?.role === 'admin');
  const deviceRecordedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shopifyOrderId || isAdmin) return;
    if (deviceRecordedRef.current === shopifyOrderId) return;
    deviceRecordedRef.current = shopifyOrderId;
    ordersApi.recordDevice(shopifyOrderId, detectDevice()).catch(() => {});
  }, [shopifyOrderId, isAdmin]);

  const [photos, setPhotos] = useState<UploadingPhoto[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [layoutResult, setLayoutResult] = useState<LayoutPlan | null>(null);
  const [duplicateWarnings, setDuplicateWarnings] = useState<string[]>([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // iOS picker-open spinner. iOS withholds the input's `change` event until
  // it has finished downloading every selected iCloud original — many seconds
  // for a big selection. We arm a "Chargement…" overlay BEFORE the picker
  // opens so the page never looks frozen. Cleared on `change` / `cancel` /
  // safety timeout. iOS-only.
  const [picking, setPicking] = useState(false);
  // Picker-open overlay safety: the native picker fires NO event on cancel, so
  // the "Chargement…" overlay (shown on iOS while the picker is open + iCloud
  // originals download) must be torn down by independent nets — see openPicker
  // (arms a hard timeout) + the cancel/focus effect below. pickerArmedRef is
  // true from open until the picker resolves (change or cancel), letting the
  // focus fallback tell a real cancel from a normal selection.
  const PICKER_SAFETY_MS = 12000;
  const pickerArmedRef = useRef(false);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPicking = useCallback(() => {
    pickerArmedRef.current = false;
    if (safetyTimerRef.current) { clearTimeout(safetyTimerRef.current); safetyTimerRef.current = null; }
    setPicking(false);
  }, []);
  // Visible feedback when a selection can't be added (iOS empty FileList,
  // zero-byte iCloud placeholders, or non-images) — replaces a silent return.
  const [addNotice, setAddNotice] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const queueRef = useRef<UploadingPhoto[]>([]);
  const activeCountRef = useRef(0);
  const sessionCreatingRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    document.title = 'Ajouter des photos — Storiz';
  }, []);

  useEffect(() => {
    return () => {
      photos.forEach((p) => { if (p.preview) URL.revokeObjectURL(p.preview); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Fetch order data ───────────────────────────────────────────────────

  const { data: orderData } = useQuery({
    queryKey: ['order', shopifyOrderId],
    queryFn: () => ordersApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
  });

  const order = orderData?.order;

  const destination =
    albumIndex === 1
      ? order?.destination_1
      : albumIndex === 2
        ? order?.destination_2
        : order?.destination_3;

  const year =
    albumIndex === 1
      ? order?.year_1
      : albumIndex === 2
        ? order?.year_2
        : order?.year_3;

  // ─── Fetch existing session ─────────────────────────────────────────────

  const { data: existingSession } = useQuery({
    queryKey: ['session', shopifyOrderId, albumIndex],
    queryFn: () => sessionsApi.get(shopifyOrderId!, albumIndex),
    enabled: !!shopifyOrderId,
  });

  useEffect(() => {
    if (existingSession?.session?.id) {
      setSessionId(existingSession.session.id);
    }
  }, [existingSession]);

  // ─── Session creation ───────────────────────────────────────────────────

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionIdRef.current) return sessionIdRef.current;

    if (sessionCreatingRef.current) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (sessionIdRef.current) {
            clearInterval(interval);
            resolve(sessionIdRef.current);
          }
        }, 100);
      });
    }

    sessionCreatingRef.current = true;
    try {
      const result = await sessionsApi.upsert(shopifyOrderId!, albumIndex, {
        destination: destination ?? null,
        year: year ?? null,
        status: 'uploading',
      });
      const id = result.session.id;
      setSessionId(id);
      sessionIdRef.current = id;
      return id;
    } finally {
      sessionCreatingRef.current = false;
    }
  }, [shopifyOrderId, albumIndex, destination, year]);

  // ─── Upload pipeline ────────────────────────────────────────────────────

  const updatePhoto = useCallback(
    (localId: string, updates: Partial<UploadingPhoto>) => {
      setPhotos((prev) =>
        prev.map((p) => (p.id === localId ? { ...p, ...updates } : p)),
      );
    },
    [],
  );

  const processUpload = useCallback(
    async (photo: UploadingPhoto) => {
      const localId = photo.id;
      // `file` is only ever released to null AFTER 'done'; a photo entering the
      // upload pipeline always still holds its source bytes. Guard defensively.
      const sourceFile = photo.file;
      if (!sourceFile) {
        activeCountRef.current--;
        processQueue();
        return;
      }

      try {
        const sid = await ensureSession();

        updatePhoto(localId, { status: 'uploading', progress: 0 });

        // Resolve file: HEIC → JPEG via pre-conversion cache
        let fileToUpload: File = sourceFile;
        let contentType = resolveContentType(sourceFile);

        if (isHeicFile(sourceFile)) {
          try {
            fileToUpload = await startHeicConversion(photo.id, sourceFile);
            contentType = 'image/jpeg';
          } catch (convErr) {
            console.warn(`[Upload] HEIC conversion failed for ${sourceFile.name}`, convErr);
            // Fall through — upload original HEIC
          }
        } else {
          // Bake EXIF orientation into the pixels first so downstream
          // consumers (pdf-lib, cover-fit math) see upright frames.
          fileToUpload = await normalizeJpegOrientation(sourceFile);
          // For regular photos, also resize if needed (large phone photos)
          const originalBlob = new Blob([await fileToUpload.arrayBuffer()], { type: fileToUpload.type });
          try {
            const resized = await resizeJpegBlob(originalBlob, MAX_PHOTO_DIM, JPEG_QUALITY);
            if (resized.size < fileToUpload.size * 0.95) {
              // Worth it: at least 5% smaller
              fileToUpload = new File([resized], fileToUpload.name, { type: fileToUpload.type });
            }
          } catch {
            // Keep original
          }
        }

        const content_hash = await sha256Hex(fileToUpload).catch(() => null);
        const resp = await photosApi.getUploadUrl(sid, fileToUpload.name, contentType, {
          content_hash,
          file_size_kb: Math.round(fileToUpload.size / 1024),
        });

        // Silent dedup (FIX 1): same file already in this album → drop it and
        // surface the count via the existing "doublons ignorés" notice. No row
        // or storage object was created server-side.
        if (resp.duplicate) {
          setDuplicateWarnings((w) => [...w, fileToUpload.name]);
          setPhotos((prev) => prev.filter((p) => p.id !== localId));
          return;
        }
        if (!resp.upload_url || !resp.photo_id) {
          throw new Error('Réponse upload-url invalide');
        }
        const { upload_url, photo_id } = resp;
        const tusBlock = resp.tus;

        // The worker created the photo row before this PUT — if the PUT fails,
        // best-effort delete the orphan row so it doesn't render as a broken
        // slot or inflate the admin count. See lib/uploadOrphanCleanup.
        // Prefer RESUMABLE (TUS) when available; on ANY TUS error, fall back to
        // the existing signed-URL PUT. Both run inside putOrCleanupOrphan so a
        // total failure still cleans the orphan row.
        const onPutProgress = (percent: number) => updatePhoto(localId, { progress: percent });
        await putOrCleanupOrphan(
          photo_id,
          async () => {
            if (tusBlock) {
              try {
                await uploadViaTus(fileToUpload, {
                  storageHost: tusBlock.storage_host,
                  anonKey: tusBlock.anon_key,
                  token: tusBlock.token,
                  bucket: tusBlock.bucket,
                  objectPath: tusBlock.object_path,
                  contentType,
                  onProgress: onPutProgress,
                  getFreshToken: () => photosApi.tusRefresh(tusBlock.object_path),
                });
                return;
              } catch {
                onPutProgress(0);
              }
            }
            await uploadToSignedUrl(upload_url, fileToUpload, contentType, onPutProgress);
          },
          (id) => photosApi.delete(id),
        );

        // Analyze — use converted JPEG for face detection + EXIF
        updatePhoto(localId, { status: 'analyzing', progress: 100 });

        const authHeaders = await getAuthHeaders();
        const analysis = await analyzePhoto(
          fileToUpload, // Use converted file for face detection
          photo_id,
          API_BASE,
          authHeaders,
        );

        // CONFIRMED in storage + analysed → release the held source bytes so a
        // large batch doesn't pin every original in RAM (parity with
        // AlbumFlowPage). The HEIC conversion cache for this id is also no longer
        // needed. Nothing re-reads `file` past 'done'.
        heicConversionCache.delete(localId);
        updatePhoto(localId, { status: 'done', analysis, file: null });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erreur inconnue';
        console.error(`[Upload] Error: ${photo.name}`, err);
        updatePhoto(localId, { status: 'error', error: message });
      } finally {
        activeCountRef.current--;
        processQueue();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ensureSession, updatePhoto],
  );

  const processQueue = useCallback(() => {
    while (
      activeCountRef.current < CONCURRENT_UPLOADS &&
      queueRef.current.length > 0
    ) {
      const next = queueRef.current.shift()!;
      activeCountRef.current++;
      processUpload(next);
    }
  }, [processUpload]);

  // ─── Clear all photos ──────────────────────────────────────────────────

  const clearAllPhotos = useCallback(() => {
    setPhotos((prev) => {
      prev.forEach((p) => { if (p.preview) URL.revokeObjectURL(p.preview); });
      return [];
    });
    queueRef.current = [];
    activeCountRef.current = 0;
    heicConversionCache.clear();
    setShowClearConfirm(false);
    setLayoutResult(null);
  }, []);

  // ─── File handling ──────────────────────────────────────────────────────

  const addFiles = useCallback(
    (fileList: FileList | File[]) => {
      // Surface why a selection produced nothing instead of returning silently
      // (empty FileList / iCloud not-ready / non-image). See lib/uploadFileFilter.
      setAddNotice(null);
      const verdict = classifyUploadSelection(fileList);
      const notice = uploadNoticeFor(verdict);
      if (!notice.proceed) { setAddNotice(notice.blocked); return; }
      if (notice.partial) setAddNotice(notice.partial);
      let files = verdict.accepted;

      // iOS per-pick cap (parity with AlbumFlowPage). Cap each pick to 20 on
      // iPhone only; surface an over-cap notice so the rest go in the next lot.
      // Non-iOS is unchanged.
      if (isIosDevice() && files.length > IOS_PICK_CAP) {
        files = files.slice(0, IOS_PICK_CAP);
        setAddNotice(`Seules les ${IOS_PICK_CAP} premières ont été prises — ajoutez le reste au lot suivant.`);
      }

      // Dedup: name + size + lastModified (cross-session reliable). Uses the
      // captured `key` (not `p.file`) so dedup still catches re-picks of photos
      // whose source bytes were already released to null after 'done'.
      const existingKeys = new Set(
        photos.map((p) => p.key),
      );
      const duplicates: string[] = [];
      const uniqueFiles = files.filter((f) => {
        const k = fileKey(f);
        if (existingKeys.has(k)) {
          duplicates.push(f.name);
          return false;
        }
        existingKeys.add(k);
        return true;
      });

      if (duplicates.length > 0) {
        setDuplicateWarnings(duplicates);
        setTimeout(() => setDuplicateWarnings([]), 6000);
      }

      if (uniqueFiles.length === 0) return;

      const currentCount = photos.length;
      const remaining = MAX_PHOTOS - currentCount;
      if (remaining <= 0) return;

      const toAdd = uniqueFiles.slice(0, remaining);

      // Create photo state — HEIC previews start empty (thumbnail extracted async)
      const newPhotos: UploadingPhoto[] = toAdd.map((file) => ({
        id: generateLocalId(),
        file,
        key: fileKey(file),
        name: file.name,
        preview: isHeicFile(file) ? '' : URL.createObjectURL(file),
        status: (isHeicFile(file) ? 'converting' : 'pending') as UploadingPhoto['status'],
        progress: 0,
      }));

      setPhotos((prev) => [...prev, ...newPhotos]);

      // Non-HEIC: add to queue immediately
      const nonHeic = newPhotos.filter((p) => p.file && !isHeicFile(p.file));
      queueRef.current.push(...nonHeic);
      processQueue();

      // HEIC: extract thumbnail instantly + start full conversion. These are
      // freshly created photos, so `file` is guaranteed non-null here.
      for (const photo of newPhotos) {
        if (!photo.file || !isHeicFile(photo.file)) continue;
        const heicFile = photo.file;

        // 1. Extract embedded thumbnail immediately for fast preview
        extractHeicThumbnail(heicFile).then((thumbUrl) => {
          if (thumbUrl) {
            setPhotos((prev) =>
              prev.map((p) =>
                p.id === photo.id && p.status === 'converting'
                  ? { ...p, preview: thumbUrl }
                  : p,
              ),
            );
          }
        });

        // 2. Start full HEIC→JPEG conversion (semaphore-limited to HEIC_CONCURRENT at a time)
        startHeicConversion(photo.id, heicFile)
          .then((convertedFile) => {
            const previewUrl = URL.createObjectURL(convertedFile);
            // Revoke old thumbnail preview if it was extracted
            setPhotos((prev) => {
              const existing = prev.find((p) => p.id === photo.id);
              if (existing?.preview && existing.preview !== '') {
                URL.revokeObjectURL(existing.preview);
              }
              return prev.map((p) =>
                p.id === photo.id
                  ? { ...p, preview: previewUrl, status: 'pending' as const }
                  : p,
              );
            });
            queueRef.current.push({ ...photo, status: 'pending', preview: previewUrl });
            processQueue();
          })
          .catch(() => {
            // Conversion failed — upload original HEIC
            setPhotos((prev) =>
              prev.map((p) =>
                p.id === photo.id ? { ...p, status: 'pending' as const } : p,
              ),
            );
            queueRef.current.push({ ...photo, status: 'pending' });
            processQueue();
          });
      }
    },
    [photos, processQueue],
  );

  // ─── Drag & drop ──────────────────────────────────────────────────────

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // A real `change` means the picker resolved: selection won → disarm the
      // cancel fallbacks and drop the picker-open overlay (the per-photo upload
      // grid takes over). Empty FileList = nothing chosen → just clear.
      clearPicking();
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
        e.target.value = '';
      }
    },
    [addFiles, clearPicking],
  );

  // Three independent nets so the picker-open overlay can NEVER hang when the
  // picker is dismissed with no selection: (1) the standard `cancel` event
  // (modern iOS 16.4+/Chrome/Firefox), (2) focus/visibility return while still
  // armed (older devices that fire no `cancel`), (3) a hard timeout backstop
  // armed in openPicker. A real selection disarms via the change handler first,
  // so none of these fire on the happy path.
  useEffect(() => {
    if (!picking) return;
    const el = fileInputRef.current;
    const onCancel = () => clearPicking();
    const onReturn = () => {
      if (document.hidden) return;
      // Defer so a real `change` (which can lag the focus return on iOS while
      // iCloud originals finish downloading) disarms first and wins.
      setTimeout(() => { if (pickerArmedRef.current) clearPicking(); }, 400);
    };
    el?.addEventListener('cancel', onCancel);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      el?.removeEventListener('cancel', onCancel);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [picking, clearPicking]);

  // Clear a pending safety timer if the component unmounts mid-pick.
  useEffect(() => () => { if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current); }, []);

  // Open the picker. Arms the iOS overlay (bridges the picker-open → change gap
  // while iCloud originals download) + the cancel fallbacks + the hard timeout,
  // THEN opens. Shared by both click targets (dropzone div + "Parcourir").
  const openPicker = useCallback(() => {
    pickerArmedRef.current = true;
    if (isIosDevice()) setPicking(true);
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    safetyTimerRef.current = setTimeout(clearPicking, PICKER_SAFETY_MS);
    fileInputRef.current?.click();
  }, [clearPicking]);

  // ─── Remove photo ────────────────────────────────────────────────────

  const removePhoto = useCallback((localId: string) => {
    setPhotos((prev) => {
      const photo = prev.find((p) => p.id === localId);
      if (photo?.preview) URL.revokeObjectURL(photo.preview);
      return prev.filter((p) => p.id !== localId);
    });
    queueRef.current = queueRef.current.filter((p) => p.id !== localId);
    heicConversionCache.delete(localId);
  }, []);

  // ─── AI layout generation ──────────────────────────────────────────────

  const [generatingStep, setGeneratingStep] = useState<string>('');
  const [generatingError, setGeneratingError] = useState<string | null>(null);

  const handleGenerateLayout = useCallback(async () => {
    if (!sessionId || !order) return;

    setGenerating(true);
    setGeneratingStep('Préparation des données...');
    setGeneratingError(null);

    try {
      const totalPages = order.page_count ?? 24;
      const layoutPref = order.layout || 'balanced';

      setGeneratingStep("Génération de la mise en page par l'IA...");
      const result = await aiApi.generateLayout(sessionId, totalPages, layoutPref, { shopify_order_id: shopifyOrderId ?? '' });
      setGeneratingStep('Mise en page terminée ! Redirection...');
      setLayoutResult(result.layout);

      setTimeout(() => {
        navigate(`/editor/${shopifyOrderId}/${albumIndexParam}/edit`);
      }, 800);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la génération';
      console.error('[Upload] AI layout error:', err);
      setGeneratingError(message);
    } finally {
      setGenerating(false);
    }
  }, [sessionId, order, shopifyOrderId, albumIndexParam, navigate]);

  // ─── Computed values ────────────────────────────────────────────────────

  const doneCount = photos.filter((p) => p.status === 'done').length;
  const errorCount = photos.filter((p) => p.status === 'error').length;
  const uploadingCount = photos.filter((p) => p.status === 'uploading').length;
  const analyzingCount = photos.filter((p) => p.status === 'analyzing').length;
  const pendingCount = photos.filter((p) => p.status === 'pending').length;
  const convertingCount = photos.filter((p) => p.status === 'converting').length;
  const inProgressCount = uploadingCount + analyzingCount + pendingCount + convertingCount;
  const allDone = photos.length > 0 && inProgressCount === 0;
  const hasPhotos = photos.length > 0;

  let progressText = '';
  if (convertingCount > 0 && uploadingCount === 0 && pendingCount === 0) {
    progressText = `Conversion HEIC... (${convertingCount} en attente, max ${HEIC_CONCURRENT} simultanées)`;
  } else if (uploadingCount > 0 || pendingCount > 0 || convertingCount > 0) {
    const uploaded = doneCount + analyzingCount;
    progressText = `Envoi de vos photos... (${uploaded}/${photos.length})`;
  } else if (analyzingCount > 0) {
    progressText = `Analyse... (${doneCount}/${photos.length})`;
  } else if (allDone) {
    progressText = `${doneCount} photo${doneCount !== 1 ? 's' : ''} prête${doneCount !== 1 ? 's' : ''}`;
  }

  const overallProgress =
    photos.length > 0
      ? Math.round(
          (photos.reduce((sum, p) => {
            if (p.status === 'done') return sum + 100;
            if (p.status === 'analyzing') return sum + 90;
            if (p.status === 'uploading') return sum + p.progress * 0.8;
            if (p.status === 'converting') return sum + 8;
            return sum;
          }, 0) /
            photos.length /
            100) *
            100,
        )
      : 0;

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* iOS picker-open overlay — visible from the tap on the dropzone until
          `change`/`cancel`/timeout, so the page never looks frozen while iOS
          downloads the customer's iCloud originals. */}
      {picking && (
        <div
          aria-live="polite"
          className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/55 backdrop-blur-sm"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full px-8 py-9 text-center">
            <div className="mx-auto w-12 h-12 mb-5 relative">
              <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
              <div className="absolute inset-0 rounded-full border-4 border-brand-500 border-t-transparent animate-spin" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Chargement de vos photos…</h3>
            <p className="text-sm text-gray-500 mt-2">ça peut prendre quelques secondes 📸</p>
          </div>
        </div>
      )}

      {/* AI Generation Modal */}
      {(generating || generatingError) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            {generating && !generatingError && (
              <>
                <div className="mx-auto w-16 h-16 mb-6 relative">
                  <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
                  <div className="absolute inset-0 rounded-full border-4 border-brand-500 border-t-transparent animate-spin" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Création de votre album</h3>
                <p className="text-sm text-gray-600 mb-4">{generatingStep}</p>
                <div className="space-y-2 mb-6 text-left">
                  <StepIndicator label="Analyse des photos" done={generatingStep !== 'Préparation des données...'} active={generatingStep === 'Préparation des données...'} />
                  <StepIndicator label="Génération par l'IA" done={generatingStep === 'Mise en page terminée !'} active={generatingStep === "Génération de la mise en page par l'IA..."} />
                  <StepIndicator label="Finalisation" done={generatingStep === 'Mise en page terminée !'} active={false} />
                </div>
                <p className="text-xs text-gray-400">Cela peut prendre jusqu'à 30 secondes...</p>
              </>
            )}
            {generatingError && (
              <>
                <div className="mx-auto w-16 h-16 mb-6 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Erreur de génération</h3>
                <p className="text-sm text-red-600 mb-6">{generatingError}</p>
                <div className="flex gap-3 justify-center">
                  <Button variant="secondary" size="sm" onClick={() => { setGeneratingError(null); setGeneratingStep(''); }}>
                    Fermer
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => { setGeneratingError(null); handleGenerateLayout(); }}>
                    Réessayer
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Clear Confirm Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
            <div className="mx-auto w-14 h-14 mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Effacer toutes les photos ?</h3>
            <p className="text-sm text-gray-500 mb-5">
              Cette action supprime les {photos.length} photos de cette vue. Les photos déjà envoyées restent sur le serveur.
            </p>
            <div className="flex gap-3">
              <Button variant="secondary" size="sm" className="flex-1" onClick={() => setShowClearConfirm(false)}>
                Annuler
              </Button>
              <Button variant="primary" size="sm" className="flex-1 !bg-red-500 hover:!bg-red-600" onClick={clearAllPhotos}>
                Effacer tout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            // PhotoUploadPage is reached from the editor's "Ajouter des
            // photos" button; back should return to the editor, not to
            // the standalone cover library (which is being retired).
            onClick={() => navigate(`/editor/${shopifyOrderId}/${albumIndexParam}/edit`)}
          >
            &larr; Retour
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-gray-900 truncate">
              {order?.shopify_order_name ?? 'Album'}
              {order?.bundle && order.bundle !== 'Solo' && (
                <span className="ml-2 text-sm text-gray-500">
                  Album {albumIndex}/{order.bundle === 'Duo' ? 2 : 3}
                </span>
              )}
            </h1>
            {destination && (
              <p className="text-xs text-gray-500 truncate">
                {destination} {year && `· ${year}`}
              </p>
            )}
          </div>
          {/* HEIC info badge */}
          <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200">
            <svg className="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-[10px] font-medium text-purple-700">Max {HEIC_CONCURRENT} HEIC simultanées</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Title */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Ajoutez vos photos</h2>
          <p className="text-gray-500 text-sm">
            Glissez vos photos ou parcourez votre appareil · JPG, PNG, WebP, HEIC supportés
          </p>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openPicker}
          className={cn(
            'relative border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors mb-6',
            isDragOver
              ? 'border-brand-500 bg-brand-500/5'
              : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50',
            photos.length >= MAX_PHOTOS && 'opacity-50 pointer-events-none',
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={galleryAccept()}
            multiple
            onChange={handleFileInputChange}
            className="hidden"
          />
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-700">Glissez vos photos ici ou cliquez pour parcourir</p>
              <p className="text-xs text-gray-400 mt-1">
                Maximum {MAX_PHOTOS} photos · HEIC converti automatiquement
              </p>
              {/* Persistent iOS batching helper (parity): iPhone RAM caps a
                  single pick at 20 photos; tell the customer up front to add
                  in lots. Self-hides off iOS. */}
              {isIosDevice() && (
                <p className="text-[11px] text-amber-600 font-medium mt-1.5">
                  20 max par sélection sur iPhone — ajoutez vos photos par lots.
                </p>
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); openPicker(); }}>
              Parcourir les fichiers
            </Button>
          </div>
        </div>

        {/* iOS-only iCloud import reassurance — right under the dropzone,
            deepens while photos are importing. Self-hides off iOS. */}
        <IosUploadNote active={inProgressCount > 0} style={{ marginBottom: 24 }} />

        {/* Overall progress */}
        {hasPhotos && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-sm font-medium text-gray-700">{progressText}</p>
              <div className="flex items-center gap-3">
                {convertingCount > 0 && (
                  <span className="text-xs text-purple-600 font-medium">
                    {convertingCount} HEIC en conversion...
                  </span>
                )}
                <p className="text-xs text-gray-400">{photos.length} / {MAX_PHOTOS}</p>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-300',
                  allDone && errorCount === 0 ? 'bg-green-500' : errorCount > 0 && allDone ? 'bg-yellow-500' : 'bg-brand-500',
                )}
                style={{ width: `${overallProgress}%` }}
              />
            </div>
            {errorCount > 0 && (
              <p className="text-xs text-red-500 mt-1">
                {errorCount} photo{errorCount !== 1 ? 's' : ''} en erreur
              </p>
            )}
          </div>
        )}

        {/* Add-files notice — explains why a selection produced nothing
            (empty FileList, still on iCloud, non-image) instead of the silent
            no-op the original handler did. */}
        {addNotice && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 mb-4">
            <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 min-w-0 text-sm text-amber-900">{addNotice}</div>
            <button onClick={() => setAddNotice(null)} className="text-amber-600 hover:text-amber-800 text-lg leading-none" aria-label="Fermer">×</button>
          </div>
        )}

        {/* Duplicate warning */}
        {duplicateWarnings.length > 0 && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 mb-4">
            <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800">
                {duplicateWarnings.length} doublon{duplicateWarnings.length > 1 ? 's' : ''} ignoré{duplicateWarnings.length > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-amber-600 mt-0.5 truncate">
                {duplicateWarnings.slice(0, 3).join(', ')}
                {duplicateWarnings.length > 3 && ` et ${duplicateWarnings.length - 3} autre${duplicateWarnings.length - 3 > 1 ? 's' : ''}`}
              </p>
            </div>
            <button className="shrink-0 text-amber-400 hover:text-amber-600" onClick={() => setDuplicateWarnings([])}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Photo grid header with clear button */}
        {hasPhotos && (
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">
              {photos.length} photo{photos.length !== 1 ? 's' : ''}
              {convertingCount > 0 && <span className="text-purple-600"> · {convertingCount} en conversion</span>}
              {doneCount > 0 && <span className="text-green-600"> · {doneCount} prête{doneCount > 1 ? 's' : ''}</span>}
            </p>
            <button
              onClick={() => setShowClearConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-red-500 hover:bg-red-50 transition-colors border border-red-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Effacer tout
            </button>
          </div>
        )}

        {/* Photo grid — shared virtualized iPhone-Photos-style square grid
            (parity with AlbumFlowPage / MagicUploadPage). Feeds the local
            in-flight `preview` blob URL as the thumbnail, a done/error badge,
            and wires the × chip to removePhoto. Per-tile upload progress lives
            in the overall progress bar + error count above; a failed photo
            shows the error badge and can be re-added via re-pick. */}
        {hasPhotos && (
          <div className="mb-6">
            <UploadedTileGrid
              photos={photos.map((photo) => ({
                id: photo.id,
                thumbUrl: photo.preview || null,
                badge:
                  photo.status === 'error'
                    ? 'error'
                    : photo.status === 'done'
                      ? 'done'
                      : null,
              }))}
              onDelete={removePhoto}
            />
          </div>
        )}

        {/* Layout result */}
        {layoutResult && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6 text-center">
            <div className="text-3xl mb-2">✓</div>
            <h3 className="text-lg font-bold text-green-800 mb-1">Mise en page générée</h3>
            <p className="text-sm text-green-700 mb-3">
              {layoutResult.pages.length} pages créées avec{' '}
              {layoutResult.pages.reduce((s, p) => s + p.photos.length, 0)} photos
              {layoutResult.unassigned_photos.length > 0 && (
                <> · {layoutResult.unassigned_photos.length} photo{layoutResult.unassigned_photos.length !== 1 ? 's' : ''} non placée{layoutResult.unassigned_photos.length !== 1 ? 's' : ''}</>
              )}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate(`/editor/${shopifyOrderId}/${albumIndexParam}/edit`)}
            >
              Ouvrir l'éditeur
            </Button>
          </div>
        )}
      </main>

      {/* Bottom action bar */}
      {allDone && doneCount > 0 && !layoutResult && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 z-10">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              {doneCount} photo{doneCount !== 1 ? 's' : ''} prête{doneCount !== 1 ? 's' : ''}
              {errorCount > 0 && <span className="text-red-500 ml-2">({errorCount} erreur{errorCount > 1 ? 's' : ''})</span>}
            </p>
            {isAddMode ? (
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate(`/editor/${shopifyOrderId}/${albumIndexParam}/edit`)}
                disabled={doneCount === 0}
              >
                Ajouter à l'album
              </Button>
            ) : (
              <Button
                variant="primary"
                size="lg"
                loading={generating}
                onClick={handleGenerateLayout}
                disabled={generating || doneCount === 0}
              >
                {generating ? 'Génération en cours...' : "Générer l'album"}
              </Button>
            )}
          </div>
        </div>
      )}

      {allDone && doneCount > 0 && !layoutResult && <div className="h-16" />}

      {/* Unified support widget. Lifts above the bottom action bar when it's
          showing (bar ≈ 64px) so it never sits on the CTA. */}
      <HelpFAB bottomOffset={allDone && doneCount > 0 && !layoutResult ? 76 : 16} />
    </div>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ label, done, active }: { label: string; done: boolean; active: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <div className={cn(
        'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors',
        done ? 'bg-green-500 text-white' : active ? 'bg-brand-500 text-white animate-pulse' : 'bg-gray-200 text-gray-400',
      )}>
        {done ? (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        ) : active ? (
          <div className="w-2 h-2 rounded-full bg-white" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-gray-300" />
        )}
      </div>
      <span className={cn('text-sm', done ? 'text-green-700 font-medium' : active ? 'text-gray-900 font-medium' : 'text-gray-400')}>
        {label}
      </span>
    </div>
  );
}
